import { Add } from '@mui/icons-material';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import React from 'react';

import { ButtonIcons } from '@/components/ButtonIcons';
import { formatDurationRussian } from '@/utils/formatDurationRussian';

import { AllDaySection } from '../AllDaySection';
import { TimeGrid } from '../TimeGrid';
import type { CalendarEventWithColor, DayColumn, WeekViewData } from '../../types';

import s from './CalendarViewWidget.module.scss';

interface CalendarViewWidgetProps {
  weekData: WeekViewData;
  onEventClick: (event: CalendarEventWithColor) => void;
  onAddLogClick?: (date: DateTime) => void;
  dailyWorkMinutes?: Record<string, number>;
  isBlurred?: boolean;
  /** Показывать «Создать черновик» в попапе выделения (только при просмотре себя) */
  showCreateDraftOption?: boolean;
  /** Только просмотр: без выделения времени мышью */
  readOnly?: boolean;
}

const WEEK_DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const CalendarViewWidget: React.FC<CalendarViewWidgetProps> = observer(
  ({
    weekData,
    onEventClick,
    onAddLogClick,
    dailyWorkMinutes,
    isBlurred = false,
    showCreateDraftOption = true,
    readOnly = false,
  }) => {
    return (
      <div className={clsx(s.calendar_widget, { [s.calendar_widget_blurred]: isBlurred })}>
        {/* Week header with day names and dates */}
        <div className={s.calendar_widget__header}>
          {/* Empty cell for time column alignment */}
          <div className={s.calendar_widget__header_spacer} />

          {/* Day headers */}
          <div className={s.calendar_widget__day_headers}>
            {weekData.days.map((day, index) => {
              const dateStr = day.date.toISODate();
              const minutes = dateStr && dailyWorkMinutes ? dailyWorkMinutes[dateStr] || 0 : 0;

              return (
                <DayHeader
                  key={index}
                  day={day}
                  dayName={WEEK_DAYS[index]}
                  totalMinutes={minutes}
                  onAddLogClick={onAddLogClick}
                />
              );
            })}
          </div>
        </div>

        {/* All-day events section */}
        <AllDaySection days={weekData.days} onEventClick={onEventClick} />

        {/* Time grid with events */}
        <TimeGrid
          days={weekData.days}
          onEventClick={onEventClick}
          showCreateDraftOption={showCreateDraftOption}
          readOnly={readOnly}
        />
      </div>
    );
  },
);

interface DayHeaderProps {
  day: DayColumn;
  dayName: string;
  totalMinutes: number;
  onAddLogClick?: (date: DateTime) => void;
}

const DayHeader: React.FC<DayHeaderProps> = ({ day, dayName, totalMinutes, onAddLogClick }) => {
  const isToday = day.date.hasSame(DateTime.local(), 'day');
  const isWeekend = day.date.weekday >= 6;
  const dayNumber = day.date.day;

  return (
    <div
      className={clsx(s.calendar_widget__day_header, {
        [s.calendar_widget__day_header_today]: isToday,
        [s.calendar_widget__day_header_weekend]: isWeekend && !isToday,
      })}
    >
      <div className={s.calendar_widget__day_info}>
        <span className={s.calendar_widget__day_name}>{dayName}</span>
        <span
          className={clsx(s.calendar_widget__day_number, {
            [s.calendar_widget__day_number_today]: isToday,
          })}
        >
          {dayNumber}
        </span>
      </div>
      <div className={s.calendar_widget__day_actions}>
        <span
          className={clsx(s.calendar_widget__day_hours, {
            [s.calendar_widget__day_hours_today]: isToday,
          })}
        >
          {formatDurationRussian(totalMinutes)}
        </span>
        {onAddLogClick && (
          <ButtonIcons
            onClick={() => onAddLogClick(day.date)}
            isAccentColor={isToday}
            sx={{ minWidth: '24px', minHeight: '24px', padding: 0 }}
          >
            <Add sx={{ fontSize: 18 }} />
          </ButtonIcons>
        )}
      </div>
    </div>
  );
};

export { CalendarViewWidget };
